// INC2-CONTACT (build order increment 2 C; CRM design §2.1, §2.5, §2.6): the thin contact core's
// row contracts. A contact is one person outside the company; its identities (email, phone, the
// ids it has in GHL or HubSpot) are what every later piece resolves to it by.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  ConsentBasis,
  ConsentChannel,
  ConsentStatus,
  ContactDuplicateState,
  ContactIdentityKind,
  ContactLifecycle,
  ContactSource,
} from "./enums";
import { Timestamp, Uuid } from "./primitives";

const stamps = { created_at: Timestamp, updated_at: Timestamp };

export const Organisation = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    domain: z.string().nullable(),
    website: z.string().nullable(),
    source: ContactSource,
    source_ref: z.string().nullable(),
    archived_at: Timestamp.nullable(),
    version: z.int(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "Organisation" });
export type Organisation = z.infer<typeof Organisation>;

export const Contact = z
  .object({
    company_id: Uuid,
    id: Uuid,
    display_name: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    title: z.string().nullable(),
    timezone: z.string().nullable(),
    organisation_id: Uuid.nullable(),
    lifecycle: ContactLifecycle,
    owner_membership_id: Uuid.nullable(),
    source: ContactSource,
    source_ref: z.string().nullable(),
    /** Some channel's latest consent is `opted_out`. */
    dnd: z.boolean(),
    merged_into_id: Uuid.nullable(),
    archived_at: Timestamp.nullable(),
    last_activity_at: Timestamp.nullable(),
    version: z.int(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "Contact" });
export type Contact = z.infer<typeof Contact>;

export const ContactIdentity = z
  .object({
    company_id: Uuid,
    id: Uuid,
    contact_id: Uuid.nullable(),
    organisation_id: Uuid.nullable(),
    membership_id: Uuid.nullable(),
    /** Always `person` when a membership holds it. */
    membership_kind: z.literal("person").nullable(),
    kind: ContactIdentityKind,
    value: z.string(),
    provider: z.string().nullable(),
    external_account: z.string().nullable(),
    external_object: z.string().nullable(),
    connection_id: Uuid.nullable(),
    is_primary: z.boolean(),
    verified_at: Timestamp.nullable(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "ContactIdentity" });
export type ContactIdentity = z.infer<typeof ContactIdentity>;

export const ContactConsent = z
  .object({
    company_id: Uuid,
    id: Uuid,
    contact_id: Uuid,
    channel: ConsentChannel,
    status: ConsentStatus,
    basis: ConsentBasis,
    evidence: z.string().nullable(),
    recorded_at: Timestamp,
    ...ActorShortColumns.shape,
    created_at: Timestamp,
  })
  .meta({ id: "ContactConsent" });
export type ContactConsent = z.infer<typeof ContactConsent>;

export const ContactDuplicate = z
  .object({
    company_id: Uuid,
    id: Uuid,
    a_id: Uuid,
    b_id: Uuid,
    why: z.string(),
    state: ContactDuplicateState,
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "ContactDuplicate" });
export type ContactDuplicate = z.infer<typeof ContactDuplicate>;
